import { useState } from "react";
import { Box, Stack } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
} from "stream-chat-react";
import { CreateDirectPaymentDrawer } from "Components/Chat/CreateDirectPaymentDrawer";
import { MultiPayerPaymentRequestAction } from "Components/Chat/MultiPayerPaymentRequestAction";
import { MultiPayerPaymentRequestCardDetails } from "Components/Chat/MultiPayerPaymentRequestCardDetails";
import type { MultiPayerPaymentRequestAttachment } from "Components/Chat/chatAttachmentTypes";
import { canLiteViewerPay } from "Components/Chat/chatPaymentRules";
import { resolveMultiPayerRequestState } from "Components/Chat/multiPayerRequestState";
import { useCloseMultiPayerRequest } from "Hooks/Chat/useCloseMultiPayerRequest";
import { useIsFullMoonePersona } from "Hooks/useIsFullMoonePersona";
import { formatCurrency } from "Utils/CurrencyUtils";

type Props = {
  attachment: MultiPayerPaymentRequestAttachment;
};

/**
 * The card of a multi-payer payment request. There is no transaction to poll — the message is the
 * request — so everything shown comes from the attachment and from the message itself: the author
 * (message.user, already viewer-relative via ChatMessageWithDisclosedName), the fixed amount, the
 * number of payments (the thread's reply_count) and the author's own "closed" flag.
 *
 * Pay opens the direct-payment drawer with the amount locked; the resulting payment message is
 * posted as a reply in this message's thread, which "N payments" opens — the only way into a
 * thread in this chat (see chatConstants.IS_MANUAL_THREADING_ENABLED). Who may pay, and whom,
 * is decided by resolveMultiPayerRequestState (the attachment is not trusted for the recipient).
 */
export const MultiPayerPaymentRequestCard = ({ attachment }: Props) => {
  const [isPayDrawerOpen, setIsPayDrawerOpen] = useState(false);
  const { message } = useMessageContext();
  const { channel, thread } = useChannelStateContext();
  const { openThread } = useChannelActionContext();
  const { client } = useChatContext();
  const { close, isClosing } = useCloseMultiPayerRequest(channel, message.id);
  const isFullMoone = useIsFullMoonePersona();

  const { amount, currencyCode } = attachment;
  const requestState = resolveMultiPayerRequestState(
    attachment,
    message.user?.id,
    client.userID,
  );

  // Payable by the rules of the request, but not by a Lite viewer unless the author is a
  // merchant (App ADR-0013): the drawer never opens, the action explains instead.
  const isPayRestricted =
    requestState.kind === "payable" &&
    !isFullMoone &&
    !canLiteViewerPay(message.user?.accessRole);
  const kind = isPayRestricted ? "restricted" : requestState.kind;

  return (
    <Box
      className="multi-payer-request-card"
      width="100%"
      minWidth={250}
      maxWidth={350}
      borderRadius={3}
      overflow="hidden"
      p={2}
      sx={{ backgroundColor: grey[kind === "closed" ? 50 : 100] }}
    >
      <Stack gap={1} p={2} pt={1}>
        <MultiPayerPaymentRequestCardDetails
          authorName={message.user?.name}
          formattedAmount={formatCurrency({ amount, currencyCode })}
          paymentsCount={message.reply_count ?? 0}
          isThreadOpen={thread?.id === message.id}
          onOpenThread={() => openThread(message)}
        />
      </Stack>
      <MultiPayerPaymentRequestAction
        variant={kind}
        isClosing={isClosing}
        onClose={close}
        onPay={() => setIsPayDrawerOpen(true)}
      />
      {requestState.kind === "payable" && !isPayRestricted && (
        <CreateDirectPaymentDrawer
          isOpen={isPayDrawerOpen}
          onClose={() => setIsPayDrawerOpen(false)}
          channel={channel}
          recipientIdentityUserPublicID={
            requestState.recipientIdentityUserPublicID
          }
          paymentRequest={{ amount, currencyCode, messageId: message.id }}
        />
      )}
    </Box>
  );
};
